import { Injectable, inject } from '@angular/core';
import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  of,
  switchMap,
  tap
} from 'rxjs';

import { AuthService } from '@services/auth/authService.service';
import { UserBehaviorService } from '@services/user-events/user-behavior.service';

import { MenuStateService } from './menu-state.service';
import { MenuService } from './menu.service';
import { MenuItem } from '../../shared/models/side-menu.model';
import { processMenuItems } from '../../shared/utils/menu-access.utils';

@Injectable({
  providedIn: 'root'
})
export class MenuBootstrapService {
  #destroyRef = inject(DestroyRef);
  #authService = inject(AuthService);
  #userBehavior = inject(UserBehaviorService);
  #menuService = inject(MenuService);
  #menuStateService = inject(MenuStateService);

  #initialized = false;

  initialize(): void {
    if (this.#initialized) return;
    this.#initialized = true;

    combineLatest([
      this.#authService.loginCompleted$.pipe(filter((completed) => completed)),
      this.#userBehavior.selectedUser.pipe(
        filter((user) => !!user?.email),
        distinctUntilChanged((prev, curr) => prev.email === curr.email)
      )
    ])
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        switchMap(([, user]) => {
          this.#menuStateService.setMenuItems({ data: null, status: 'loading' });
          return this.#menuService.getMenusWithAccess(user.email);
        }),
        tap({
          next: (menu) => this.#applyMenu(menu),
          error: () => this.#menuStateService.setMenuItems({ data: null, status: 'error' })
        }),
        catchError(() => of(null))
      )
      .subscribe();
  }

  applyMenu(menu: MenuItem[]): void {
    this.#applyMenu(menu);
  }

  reset(): void {
    this.#menuStateService.reset();
  }

  #applyMenu(menu: MenuItem[]): void {
    const processed = processMenuItems(menu);
    this.#menuStateService.setMenuItems({ data: processed, status: 'success' });
  }
}
